/** Retention as a screen holds it: null is "not set". */
export interface RetentionValues {
  auditRetentionDays: number | null;
  engagementRetentionDays: number | null;
}

/** What the API counted the next purge would delete under a proposal; nothing is deleted to count it. */
export interface RetentionImpact {
  auditEvents: number | null;
  engagementEvents: number | null;
}

export type Confirmation = { required: false } | { required: true; message: string };

/**
 * Whether a class's retention gets shorter. Setting one that was not set counts — the purge starts deleting —
 * and clearing one does not: an unset retention is never purged, so nothing more is deleted.
 */
function shorter(current: number | null, proposed: number | null): boolean {
  if (proposed === null) return false;
  return current === null || proposed < current;
}

/**
 * What a room actually keeps: its own figure per class, or the platform's where it keeps none. A room's blank
 * box means "follow the platform", not "never purge", so whether a room's change shortens anything is judged
 * on these — clearing a room's own 730 days back to a platform keeping 400 is shortening, though the box only
 * went blank.
 */
export function effectiveRetention(room: RetentionValues, platform: RetentionValues): RetentionValues {
  return {
    auditRetentionDays: room.auditRetentionDays ?? platform.auditRetentionDays,
    engagementRetentionDays: room.engagementRetentionDays ?? platform.engagementRetentionDays,
  };
}

/** The classes a proposal shortens, which is when the screen asks the API what the purge would delete. */
export function shortened(current: RetentionValues, proposed: RetentionValues): RetentionField[] {
  const fields: RetentionField[] = [];
  if (shorter(current.auditRetentionDays, proposed.auditRetentionDays)) fields.push('audit');
  if (shorter(current.engagementRetentionDays, proposed.engagementRetentionDays)) fields.push('engagement');
  return fields;
}

const count = (n: number | null, noun: string) =>
  !n ? `no ${noun}` : `${n.toLocaleString('en-US')} ${noun}`;

/**
 * **Shortening retention is destructive, so it is confirmed first**, with what the API counted the next purge
 * would delete — counted, never deleted, by the purge's own rule. Only the classes being shortened are
 * named. Lengthening, or clearing, asks nothing: nothing more is deleted.
 */
export function confirmationFor(
  current: RetentionValues,
  proposed: RetentionValues,
  impact: RetentionImpact | null,
): Confirmation {
  const fields = shortened(current, proposed);
  if (fields.length === 0) return { required: false };

  const parts = fields.map((f) =>
    f === 'audit' ? count(impact?.auditEvents ?? null, 'audit events') : count(impact?.engagementEvents ?? null, 'usage events'));
  return {
    required: true,
    message:
      `A shorter retention is permanent once the purge runs. On its next run it would delete ${parts.join(' and ')} ` +
      'older than the new period. Nothing is deleted until then.',
  };
}

export interface RetentionText {
  figure: string;
  note?: string;
}

/**
 * A retention as the screen states it. **Not set is a state, never a figure**: the purge refuses to run on an
 * unset retention (Decision 11), and a number shown in its place — the minimum, a "suggested" default — would
 * read as a retention somebody chose while nothing was being deleted at all.
 */
export function retentionText(days: number | null): RetentionText {
  return days === null
    ? { figure: 'Not set', note: 'The purge will not run until it is set, so nothing is deleted yet.' }
    : { figure: `${days} days` };
}

export type RetentionField = 'audit' | 'engagement';

export type FieldRefusals = Partial<Record<RetentionField | 'form', string>>;

/**
 * Where a refused save is shown: beside the field it is about, in the server's own sentence (which names the
 * minimum, the floor, or the room keeping less). `retention_below_floor` is always the audit field — only
 * audit has a floor — and `retention_below_minimum` is whichever proposed figure is under the minimum the
 * API reported, so the screen holds no copy of the number. Anything else is not about a field.
 */
export function refusalByField(error: unknown, proposed: RetentionValues, minimumDays: number): FieldRefusals {
  if (!error || typeof error !== 'object') return {};
  const { code, serverMessage, message } = error as { code?: string; serverMessage?: string; message?: string };
  const text = serverMessage ?? message ?? 'The change was refused.';
  if (code === 'retention_below_floor') return { audit: text };
  if (code === 'retention_below_minimum') {
    const below = (days: number | null) => days !== null && days < minimumDays;
    const fields: FieldRefusals = {};
    if (below(proposed.auditRetentionDays)) fields.audit = text;
    if (below(proposed.engagementRetentionDays)) fields.engagement = text;
    return Object.keys(fields).length > 0 ? fields : { form: text };
  }
  return { form: text };
}

export interface OverrideRequest {
  code: string;
  scope: 'platform' | 'room' | 'operator' | 'player';
  roomId: string | null;
  operatorObjectId: string | null;
  cardId: string | null;
  enabled: boolean;
}

/**
 * An operator's override, from a directory search result: its object id and nothing typed. The server asks
 * the directory again and supplies the tenant staff tokens carry, so there is no tenant here to get wrong.
 */
export function operatorOverride(code: string, user: { objectId: string }, enabled: boolean): OverrideRequest {
  return { code, scope: 'operator', roomId: null, operatorObjectId: user.objectId, cardId: null, enabled };
}

/**
 * A player's override, by card at a room — how an administrator knows a player. The server resolves the card
 * to the account connected to it; an account id never passes through this screen.
 */
export function playerOverride(code: string, roomId: string, cardId: string, enabled: boolean): OverrideRequest {
  return { code, scope: 'player', roomId, operatorObjectId: null, cardId: cardId.trim(), enabled };
}

export interface OverridableType {
  code: string;
  label: string;
}

/**
 * The types an override may name: exactly the list the API sends, which is every active engagement type and
 * never an audit type (Decision 12). Nothing is added from anywhere else — an audit type switched off would
 * make its absence indistinguishable from nothing having happened, so the screen has no way to offer one.
 */
export function overridableTypes(types: { code: string; description: string }[]): OverridableType[] {
  return [...types]
    .sort((a, b) => a.code.localeCompare(b.code))
    .map((t) => ({ code: t.code, label: t.description }));
}

export interface OverrideTarget {
  scope: string;
  roomId: string | null;
  operatorName: string | null;
  cards: { roomName: string; cardId: string }[];
}

/**
 * Who an override is for, in words: the screen shows no id, since an administrator cannot check one against
 * anything. An operator is what a room calls them; a player is the cards connected to their account.
 */
export function overrideTargetText(o: OverrideTarget, roomName: (id: string) => string | null): string {
  switch (o.scope) {
    case 'room':
      return (o.roomId && roomName(o.roomId)) || 'A room that no longer exists';
    case 'operator':
      return o.operatorName ?? 'A staff member no room has named';
    case 'player':
      return o.cards.length === 0
        ? 'A player who holds no card now'
        : o.cards.map((c, i) => `${i === 0 ? 'Card' : 'card'} ${c.cardId} at ${c.roomName}`).join(', ');
    default:
      return 'Everyone on the platform';
  }
}
